import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, model, signal } from '@angular/core';
import { bzIconClose, bzIconSearchOutline } from '@bringoz/ui/icons';
import { BzIcon } from '../icon/bz-icon';
import { BzInput } from '../input/bz-input';

/**
 * A debounced search field. Composed from `bz-input` rather than
 * reimplementing the field chrome — the search icon and the clear button are
 * exactly what `bzInputPrefix`/`bzInputSuffix` exist for.
 *
 * It wears `bz-input`'s `filled` appearance, which is the design's Search
 * Field: a grey fill at rest, a border on hover, white plus the border while
 * focused. That fill change is how the design shows focus, which is why the
 * appearance belongs to `bz-input` rather than being painted on from here.
 *
 * `value` only settles `debounceMs` after typing stops, and only once the
 * draft is empty or has reached `minLength` — filtering on every keystroke
 * is wasted work for a query no one will see. The legacy
 * `app-bringoz-search-input` debounced at a fixed 1000ms; this defaults to
 * 300ms, which is closer to what users expect from a live filter today. The
 * displayed text itself is never debounced, so typing still feels instant.
 */
@Component({
  selector: 'bz-search-input',
  imports: [BzInput, BzIcon],
  templateUrl: './bz-search-input.html',
  styleUrl: './bz-search-input.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzSearchInput {
  /** The settled value — updates `debounceMs` after typing stops. */
  readonly value = model('');

  readonly placeholder = input('');
  readonly disabled = input(false);
  readonly debounceMs = input(300);
  readonly minLength = input(0);
  readonly ariaLabel = input('Search');

  protected readonly searchIcon = bzIconSearchOutline;
  protected readonly closeIcon = bzIconClose;

  /** What is on screen right now — updates immediately, unlike `value`. */
  protected readonly draft = signal('');

  private timer: ReturnType<typeof setTimeout> | undefined;
  private settledValue = '';

  constructor() {
    // Keeps `draft` in step with an externally-set `value` (a parent
    // resetting the search, a bound form default) without fighting the
    // debounce timer that drives the other direction.
    effect(() => {
      const value = this.value();
      // Our own emission may be observed after the next keystroke. Only a
      // parent change should cancel that newer draft and its pending timer.
      if (value === this.settledValue) return;
      this.settledValue = value;
      clearTimeout(this.timer);
      this.draft.set(value);
    });

    inject(DestroyRef).onDestroy(() => clearTimeout(this.timer));
  }

  protected onDraftInput(next: string): void {
    this.draft.set(next);
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      if (next.length === 0 || next.length >= this.minLength()) {
        this.settledValue = next;
        this.value.set(next);
      }
    }, this.debounceMs());
  }

  protected clear(): void {
    if (this.disabled()) return;
    clearTimeout(this.timer);
    this.draft.set('');
    this.settledValue = '';
    this.value.set('');
  }
}
